import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { DriverRejectionDialogButton } from "@/components/Driver/DriverRejectionDialogButton";
import DriverRequestDetails from "@/components/Driver/DriverRequestDetails";
import { ConfirmationDialogButton } from "@/components/Driver/DriverRequestDetails/confirmation-dialog-button";
import {
  useConfirmDriverMutation,
  useGetDriverByIdQuery,
  useRejectDriverRequestMutation,
} from "@/libs/generated/hooks";
import {
  DRIVER_ACTIONS,
  ERROR_MESSAGES,
  NAVIGATION_ROUTES,
  SUCCESS_MESSAGES,
  TOAST_CONFIG,
} from "@/utils/constants/driver-actions";

function PendingDriverDetails() {
  const { id } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();

  const navigate = useNavigate();
  if (!id) throw new Error("no driver id in params");
  const {
    data,
    error,
    isPending: loading,
  } = useGetDriverByIdQuery({
    GetByIdInput: {
      id: Number(id),
    },
  });
  const confirmDriver = useConfirmDriverMutation({
    onSuccess: () => {
      toast.success(t(SUCCESS_MESSAGES.CONFIRM), TOAST_CONFIG);
      navigate(NAVIGATION_ROUTES.ACCEPTED_REQUESTS);
    },
    onError: (_err: any) => {
      toast.error(t(ERROR_MESSAGES.GENERIC), TOAST_CONFIG);
    },
  });
  const rejectDriver = useRejectDriverRequestMutation({
    onSuccess: () => {
      toast.success(t(SUCCESS_MESSAGES.REJECT), TOAST_CONFIG);
      navigate(NAVIGATION_ROUTES.REJECTED_REQUESTS);
    },
    onError: (_err: any) => {
      toast.error(t(ERROR_MESSAGES.REJECT), TOAST_CONFIG);
    },
  });

  const isConfirmingDriver = confirmDriver.isPending;
  const isRejectingDriver = rejectDriver.isPending;

  if (loading) return <p>{t("common.loading")}</p>;

  if (error) {
    return <p>{t("common.error.generic")}</p>;
  }
  if (!data?.getDriverById) {
    return <p>{t("common.no-data")}</p>;
  }
  const driverData = data.getDriverById;

  const handleConfirmClick = () => {
    // Check for rejected documents before opening the modal
    // Need to parse the docs the same way the form does
    const rawDocs = driverData.docs as any[] | undefined;
    if (!rawDocs) return true;
    
    // Find any document with REJECTED status
    const rejectedDoc = rawDocs.find((doc) => doc?.version_status === "REJECTED");
    
    if (rejectedDoc) {
      // Get the document name/id for the error message and scrolling
      const docId = (rejectedDoc.metadata?.id as string | undefined) || rejectedDoc.label || rejectedDoc.name || rejectedDoc.key || "document";
      const docLabel = rejectedDoc.label || rejectedDoc.name || "document";
      
      toast.error(
        `Impossible d'accepter le chauffeur : le document "${docLabel}" a été rejeté. Veuillez demander au chauffeur de soumettre un nouveau document.`,
        { ...TOAST_CONFIG, autoClose: 7000 }
      );
      
      // Scroll to the rejected document using the metadata id
      setTimeout(() => {
        const normalizedId = docId.toLowerCase().replace(/[_\s]/g, '-');
        // Try multiple selectors to find the document
        let targetElement = document.querySelector(`[data-document-name="${docId}"]`);
        if (!targetElement) {
          targetElement = document.querySelector(`[data-document-name="${normalizedId}"]`);
        }
        if (!targetElement) {
          // Fallback: find by looking for the rejection reason text in document items
          const allItems = document.querySelectorAll('[data-document-name]');
          for (const item of allItems) {
            const hasRejection = item.querySelector('[class*="MuiChip"][class*="error"]');
            if (hasRejection) {
              targetElement = item;
              break;
            }
          }
        }
        
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Trigger the highlight animation
          const listItem = targetElement.querySelector('.MuiListItem-root');
          if (listItem) {
            (listItem as HTMLElement).style.borderColor = '#ed6c02';
            (listItem as HTMLElement).style.animation = 'document-highlight 1.2s ease-in-out 3';
          }
        }
      }, 300);
      
      return false; // Prevent modal from opening
    }
    
    return true; // Allow modal to open
  };

  return (
    <DriverRequestDetails
      driverData={driverData}
      showTerms={true}
      actions={
        <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "flex-end" }}>
          <ConfirmationDialogButton
            buttonText={DRIVER_ACTIONS.CONFIRM.buttonText}
            title={DRIVER_ACTIONS.CONFIRM.title}
            description={DRIVER_ACTIONS.CONFIRM.confirmation}
            buttonColor={theme.palette.success.main}
            icon={DRIVER_ACTIONS.CONFIRM.icon}
            actionIsLoading={isConfirmingDriver}
            onBeforeOpen={handleConfirmClick}
            onConfirm={() => {
              confirmDriver.mutate({ input: { driverId: driverData.id } });
            }}
          />
          <DriverRejectionDialogButton
            description={t(DRIVER_ACTIONS.REJECT.confirmation)}
            icon={DRIVER_ACTIONS.REJECT.icon}
            loading={isRejectingDriver}
            onConfirm={(reason) => {
              rejectDriver.mutate({ input: { email: driverData.email, reason } });
            }}
          />
        </Box>
      }
    />
  );
}

export default PendingDriverDetails;
